/**
 * Tank Store - Layered Architecture
 *
 * Domain 모델 사용으로 변경
 */

import { create } from 'zustand'
import type { Tank } from '@/types/domain'
import { getTanks } from '@/api/tank/tank.service'

// ============================================
// Legacy Type (Backward Compatibility)
// ============================================

/**
 * @deprecated Use Tank from @/types/domain instead
 */
export type TankInfo = {
  size: number
  location: string
  tankName: string
  maxDensity: number
  volume?: number
  radius?: number
  depth?: number
}

/**
 * @deprecated Use Record<string, Tank> instead
 */
export type TankState = { [tankId: string]: TankInfo }

// ============================================
// New Store Definition
// ============================================

interface TankStore {
  // State
  tanks: Record<string, Tank>
  emptyTanks: Tank[] // 빈 탱크 목록 (GET /tank/empty)
  isLoading: boolean
  error: Error | null

  // Actions
  setTanks: (tanks: Record<string, Tank>) => void
  setEmptyTanks: (tanks: Tank[]) => void
  loadTanks: () => Promise<void>
  getTankById: (tankId: string) => Tank | undefined
  addTank: (tank: Tank) => void
  updateTank: (tankId: string, tank: Partial<Tank>) => void
  removeTank: (tankId: string) => void
  reset: () => void
}

const initialState = {
  tanks: {},
  emptyTanks: [] as Tank[],
  isLoading: false,
  error: null,
}

export const useTankStore = create<TankStore>((set, get) => ({
  ...initialState,

  // ===== Setters =====

  setTanks: (tanks) => {
    set({ tanks, error: null })
  },

  setEmptyTanks: (emptyTanks) => {
    set({ emptyTanks, error: null })
  },

  // ===== Async Actions =====

  loadTanks: async () => {
    set({ isLoading: true, error: null })
    try {
      const tankList = await getTanks() // Service Layer 사용

      // Array → Record 변환
      const tanksRecord = tankList.reduce((acc, tank) => {
        acc[tank.id] = tank
        return acc
      }, {} as Record<string, Tank>)

      set({ tanks: tanksRecord, isLoading: false })
    } catch (error) {
      set({ error: error as Error, isLoading: false })
      throw error
    }
  },

  // ===== Getters =====

  getTankById: (tankId) => {
    return get().tanks[tankId]
  },

  // ===== CRUD Actions =====

  addTank: (tank) => {
    set((state) => ({
      tanks: {
        ...state.tanks,
        [tank.id]: tank,
      },
    }))
  },

  updateTank: (tankId, tankUpdate) => {
    set((state) => ({
      tanks: {
        ...state.tanks,
        [tankId]: {
          ...state.tanks[tankId],
          ...tankUpdate,
        },
      },
    }))
  },

  removeTank: (tankId) => {
    set((state) => {
      const newTanks = { ...state.tanks }
      delete newTanks[tankId]
      return { tanks: newTanks }
    })
  },

  reset: () => {
    set(initialState)
  },
}))
